import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useRouteContext } from '@tanstack/react-router';
import { useState } from 'react';
import { Button, ConfirmDialog, CopyId, ErrorState } from '@bb/ui';
import { ApiError, type Dependent } from '../api/client.ts';
import { Can } from '../features/access/Can.tsx';
import { CHANNEL_LABEL } from './SubscriptionGroupsPage.tsx';

const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');

/** A small line chart of the group size; the numbers are also in the table below it. */
function SizeChart({ points }: { points: Array<{ day: string; subscribed: number; unsubscribed: number }> }) {
  if (points.length < 2) return <p className="hint">Not enough days for a chart yet.</p>;
  const max = Math.max(...points.map((p) => Math.max(p.subscribed, p.unsubscribed)), 1);
  const x = (i: number) => (i / (points.length - 1)) * 300;
  const y = (v: number) => 80 - (v / max) * 76;
  const line = (key: 'subscribed' | 'unsubscribed') => points.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p[key]).toFixed(1)}`).join(' ');
  const last = points.at(-1)!;
  return (
    <svg viewBox="0 0 300 84" className="size-chart" role="img" aria-label={`Size over ${points.length} days: ${last.subscribed} subscribed, ${last.unsubscribed} unsubscribed on ${last.day}`}>
      <path d={line('subscribed')} className="size-chart__subscribed" fill="none" />
      <path d={line('unsubscribed')} className="size-chart__unsubscribed" fill="none" />
    </svg>
  );
}

/** One subscription group: size over 30 days, live count, API id, archive with where-used. */
export function SubscriptionGroupPage() {
  const { environmentId, groupId } = useParams({ from: '/env/$environmentId/audience/subscription-groups/$groupId' });
  const { api } = useRouteContext({ from: '/env/$environmentId/audience/subscription-groups/$groupId' });
  const queryClient = useQueryClient();
  const group = useQuery({ queryKey: ['subscription-group', environmentId, groupId], queryFn: () => api.getSubscriptionGroup(environmentId, groupId) });
  const stats = useQuery({ queryKey: ['subscription-group-stats', environmentId, groupId], queryFn: () => api.subscriptionGroupStats(environmentId, groupId, 30) });
  const calculate = useMutation({ mutationFn: () => api.calculateSubscriptionGroup(environmentId, groupId) });
  const [confirming, setConfirming] = useState(false);
  const archive = useMutation({
    mutationFn: () => api.archiveSubscriptionGroup(environmentId, groupId),
    onSuccess: (row) => {
      queryClient.setQueryData(['subscription-group', environmentId, groupId], row);
      void queryClient.invalidateQueries({ queryKey: ['subscription-groups', environmentId] });
    },
    // The dialog closes either way: a refusal lists what still uses the group on the page.
    onSettled: () => setConfirming(false),
  });
  const dependents = archive.error instanceof ApiError && archive.error.code === 'object_in_use' ? ((archive.error.problem.dependents as Dependent[] | undefined) ?? []) : [];

  if (group.isPending) return <p aria-busy="true">Loading group…</p>;
  if (group.isError) return <ErrorState title="The group could not be loaded" message={errorText(group.error)} />;
  const g = group.data;

  return (
    <section>
      <h1>{g.name}</h1>
      <p>
        {CHANNEL_LABEL[g.channel]} · default for users without a choice: <strong>{g.defaultState}</strong>
        {g.allowClientUpdates ? ' · apps may change it' : ''}
        {g.status === 'archived' && <span className="tag tag--neutral"> Archived — sends to it are blocked</span>}
      </p>
      <CopyId value={g.apiId} label="API id" />

      <h2>Size, last 30 days</h2>
      {stats.isPending ? (
        <p aria-busy="true">Loading statistics…</p>
      ) : stats.isError ? (
        <p role="alert">Statistics could not be loaded.</p>
      ) : (
        <>
          <SizeChart points={stats.data} />
          <table className="table" aria-label="Daily size">
            <thead>
              <tr>
                <th scope="col">Day</th>
                <th scope="col">Subscribed</th>
                <th scope="col">Unsubscribed</th>
              </tr>
            </thead>
            <tbody>
              {stats.data.map((p) => (
                <tr key={p.day}>
                  <td>{p.day}</td>
                  <td>{p.subscribed}</td>
                  <td>{p.unsubscribed}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      <Button onClick={() => calculate.mutate()} disabled={calculate.isPending}>
        Calculate now
      </Button>
      {calculate.data && (
        <p role="status">
          Now: {calculate.data.subscribed} subscribed, {calculate.data.unsubscribed} unsubscribed.
        </p>
      )}

      {g.status === 'active' && (
        <Can permission="catalog.edit">
          <h2>Archive</h2>
          <Button variant="danger" onClick={() => setConfirming(true)}>
            Archive group
          </Button>
          {dependents.length > 0 && (
            <div role="alert">
              <p>The group is still used by:</p>
              <ul>
                {dependents.map((d) => (
                  <li key={`${d.objectType}:${d.objectId}`}>
                    {d.name} ({d.objectType})
                  </li>
                ))}
              </ul>
            </div>
          )}
          {archive.isError && dependents.length === 0 && <p role="alert">{errorText(archive.error)}</p>}
        </Can>
      )}
      <ConfirmDialog open={confirming} onOpenChange={setConfirming} title={`Archive “${g.name}”?`} confirmLabel="Archive" busy={archive.isPending} onConfirm={() => archive.mutate()}>
        <p>Sends to an archived group are blocked and it can no longer be edited.</p>
      </ConfirmDialog>
    </section>
  );
}
